import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { ChevronDownIcon } from '../../icons/ChevronDownIcon';

export type DropdownMenuItem = {
  label: string;
  optionLabel?: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
};

export type DropdownMenuProps = {
  icon?: React.ReactNode;
  label?: string;
  placeholder?: string;
  items: DropdownMenuItem[];
  className?: string;
  buttonClassName?: string;
  labelClassName?: string;
  dropdownClassName?: string;
  itemClassName?: string;
  iconFill?: string;
  iconSize?: number;
  showIcon?: boolean;
};

const DropdownMenu = ({
  icon,
  label,
  placeholder = 'Select option',
  items,
  className,
  buttonClassName,
  labelClassName,
  dropdownClassName,
  itemClassName,
  iconFill = 'currentColor',
  iconSize = 10,
  showIcon = true,
  ...props
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleItemClick = (item: DropdownMenuItem) => {
    if (item.disabled) return;
    item.onClick();
    setIsOpen(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (!dropdownRef.current?.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={classNames('relative content-center text-left', className)} ref={dropdownRef} {...props}>
      <div
        className={classNames(
          'border-gray-3 hover:bg-gray-50 flex h-10 w-full flex-row items-center gap-x-2 border px-4 text-sm font-medium',
          buttonClassName
        )}
      >
        <button
          type="button"
          className="flex w-full flex-row items-center justify-center gap-x-2"
          onClick={toggleDropdown}
          aria-label={label || placeholder}
        >
          {label && <span className={classNames('flex-shrink-0', labelClassName)}>{label}</span>}
          {icon && <span className={classNames('flex-shrink-0')}>{icon}</span>}
          {showIcon && (
            <span className="flex-shrink-0">
              <ChevronDownIcon
                width={iconSize}
                height={iconSize}
                className={iconFill.startsWith('#') ? `text-[${iconFill}]` : `text-${iconFill}`}
              />
            </span>
          )}
        </button>
      </div>
      {isOpen && (
        <div
          className={classNames(
            'absolute right-0 z-10 min-w-[250px] rounded-md border bg-white shadow-lg',
            dropdownClassName
          )}
          style={{
            borderColor: '#E7E9E9',
          }}
        >
          <ul className="max-h-60 list-none overflow-y-auto">
            {items.map((item, index) => (
              <li
                key={`${index}-${item.label}`}
                onClick={() => handleItemClick(item)}
                className={classNames(
                  'cursor-pointer rounded-md p-1 hover:bg-slate-1',
                  itemClassName,
                  item.className,
                  item.disabled && 'cursor-not-allowed !text-grey-4'
                )}
              >
                <div className="flex flex-col gap-0.5">
                  <p className={classNames('cursor-pointer', item.disabled ? 'text-sm text-grey-4' : 'text-grey-6')}>
                    {item.label}
                  </p>
                  {item.optionLabel != null && item.optionLabel !== '' && (
                    <p className="cursor-pointer text-sm leading-snug text-grey-6">{item.optionLabel}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
